import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {UserCarePlan} from "../../models/user-care-plan.model";
import {UserCarePlanItem} from "../../models/user-care-plan-item.model";
import {ModalController, PopoverController} from "@ionic/angular";
import {UserCarePlanService} from "../../providers/user-care-plan.service";
import {VetReservationService} from "../../providers/vet-reservation.service";
import {VetReservation} from "../../models/vet-reservation.model";
import {CommonUtils} from "../../utils/common";
import {User} from "../../models/user.model";
import {CancelationReasonPopoverComponent} from "../cancelation-reason-popover/cancelation-reason-popover.component";
import {Subscription} from "rxjs";
import {AppStateService} from "../../providers/app-state.service";

type UsageExtendedUserCarePlanItem = UserCarePlanItem & {
  doctorName: string;
  clinicName: string;
  startDate: string;
  startDatePassed: boolean;
  petName: string;
  userName: string;
  wellnessPlanName: string;
  vetcode: number;
};

type ColumnDef = { columnKey: string; columnLabel: string; hidden?: boolean; color?: boolean }

@Component({
  selector: 'app-user-care-plan-usage-modal',
  templateUrl: './user-care-plan-usage-modal.component.html',
  styleUrls: ['./user-care-plan-usage-modal.component.scss'],
})
export class UserCarePlanUsageModalComponent implements OnInit, OnDestroy {
  @Input() carePlanUUID: string;
  @Input() user: User;
  isLoading: boolean;
  userCarePlan: UserCarePlan;
  usedUserCarePlanItems: UserCarePlanItem[];
  usedExtendedUserCarePlanItems: UsageExtendedUserCarePlanItem[];
  columnDefs: ColumnDef[];
  colorMap: Map<string, string> = new Map();
  colorList = ['primary', 'success'];
  @Input() petName: string;
  private currentDate: Date;
  cancellationReason: string;
  subscriptions: Subscription = new Subscription();
  isBusy: boolean;


  constructor(private modalController: ModalController,
              private carePlanService: UserCarePlanService,
              private vetReservationService: VetReservationService,
              private commonUtils: CommonUtils,
              private popoverCtrl: PopoverController,
              private appStateService: AppStateService) {
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }

  registerSubscription(sub: Subscription) {
    if (sub) {
      this.subscriptions.add(sub);
    }
  }

  async ngOnInit() {
    this.registerSubscription(this.appStateService.isBusySubject.subscribe(isBusy => {
      this.isBusy = isBusy
    }));

    try {
      this.currentDate = new Date(await this.vetReservationService.getServerTime().toPromise());
    } catch (e) {
      this.currentDate = new Date();
    }

    this.columnDefs = [

      {
        columnKey: 'startDate',
        columnLabel: 'Data realizacji (data wizyty)',
        color: true
      },
      {
        columnKey: 'name',
        columnLabel: 'Usługa',
        color: true
      },
      {
        columnKey: 'doctorName',
        columnLabel: 'Lekarz'
      },
      {
        columnKey: 'clinicName',
        columnLabel: 'Lecznica'
      },
      {
        columnKey: 'realizationDate',
        columnLabel: 'Data rezerwacji'
      },
      {
        columnKey: 'wellnessPlanName',
        columnLabel: 'Pakiet',
        hidden: true
      },
      {
        columnKey: 'userName',
        columnLabel: 'Opiekun',
        hidden: true
      },
      {
        columnKey: 'petName',
        columnLabel: 'Zwierzę',
        hidden: true
      },
    ];


    this.isLoading = true;
    try {
      this.carePlanService.getUserCarePlanByUUID(this.carePlanUUID).subscribe(async userCarePlan => {
        this.userCarePlan = userCarePlan;
        console.log(userCarePlan);
        if (this.userCarePlan) {
          this.usedUserCarePlanItems = this.userCarePlan.userCarePlanItems
            .filter(item => item.realizationDate);

          this.usedExtendedUserCarePlanItems = await Promise.all(
            this.usedUserCarePlanItems.map(async item => {
              const vetReservation: VetReservation = await this.vetReservationService.getReservationByUUID(item.vetReservationUUID).toPromise();
              const visitStartDate = new Date(vetReservation.startDateTime);
              const extended: UsageExtendedUserCarePlanItem = {
                ...item,
                doctorName: vetReservation.doctorName,
                clinicName: vetReservation.vetClinic.displayName,
                startDate: vetReservation.startDateTime,
                startDatePassed: visitStartDate.getTime() < this.currentDate.getTime(),
                userName: `${userCarePlan.user.firstName} ${userCarePlan.user.lastName}`,
                petName: this.petName,
                wellnessPlanName: userCarePlan.wellnessPlan.name,
                vetcode: vetReservation.vetcodeId
              };
              return extended;
            })
          );
          this.usedExtendedUserCarePlanItems.sort(((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime()));
          this.prepareColorMap();
          this.isLoading = false;
        }
      }, error => {
        this.isLoading = false;
      });
    } catch (e) {
      this.isLoading = false;
    }
  }

  onClose() {
    this.modalController.dismiss();
  }

  onSaveToCsv() {
    let rows: any[][] = [];
    let headerRow: any[] = [];
    for (const columnDef of this.columnDefs) {
      headerRow.push(columnDef.columnLabel);
    }
    rows.push(headerRow);
    for (const extendedItem of this.usedExtendedUserCarePlanItems) {
      let rowArray: any[] = [];
      for (const columnDef of this.columnDefs) {
        rowArray.push(extendedItem[columnDef.columnKey]);
      }
      rows.push(rowArray);
    }
    const csv = this.commonUtils.arrayToCsv(rows);
    const fileName = (this.userCarePlan.user.firstName && this.userCarePlan.user.lastName) ?
      `Raport_zuzycia_${this.commonUtils.sanitizeFileName(`${this.userCarePlan.user.firstName}_${this.userCarePlan.user.lastName}`)}` :
      "Raport_zuzycia";
    this.commonUtils.downloadBlob(csv, fileName, 'text/csv;charset=utf-8;');
  }

  async onCancelUsedCarePlanItem(ev, userCarePlanItem: UsageExtendedUserCarePlanItem) {
    const popover = await this.popoverCtrl.create({
      component: CancelationReasonPopoverComponent,
      event: ev,
      translucent: true,
      backdropDismiss: true,
      cssClass: 'centered-popover'
    });

    await popover.present();

    const {data} = await popover.onDidDismiss();
    if (data) {
      this.cancellationReason = data;
      this.commonUtils.presentYesNoActionSheet('Na pewno chcesz zgłosić chęć anulowania zużycia usługi z pakietu?',
        'Tak', 'Nie', 'checkmark', 'close', () => {
          const requestObj = {
            vetReservationUUID: userCarePlanItem.vetReservationUUID,
            userCarePlanItemUUID: userCarePlanItem.uuid,
            reason: this.cancellationReason,
            carePlanUUID: this.userCarePlan.uuid,
            vClientId: this.userCarePlan.vClientId
          }

          this.appStateService.updateIsBusySubject(true);
          this.registerSubscription(this.carePlanService.cancelCarePlanItem(requestObj).subscribe(res => {
            if (res) {
              userCarePlanItem.restoreRequestCreated = res.restoreRequestCreated;
              this.commonUtils.presentInfoToast("Zgłoszenie anulowania zużycia usługi zostało przyjęte.");
            } else {
              this.commonUtils.presentAlertToast("Wystąpił błąd podczas składania zgłoszenia.");
            }
            this.appStateService.updateIsBusySubject(false);
          }, error => {
            this.commonUtils.presentAlertToast("Wystąpił błąd podczas składania zgłoszenia.");
            this.appStateService.updateIsBusySubject(false);
          }));
        }, () => {
        });
    }
  }


  // async onCancelUsedCarePlanItem(ev, userCarePlanItem: UsageExtendedUserCarePlanItem) {
  //   const popover = (ev?.target as HTMLElement)?.closest('ion-popover') as HTMLIonPopoverElement;
  //
  //
  // }

  private prepareColorMap() {
    let colorMapIndex = 0;
    for (const used of this.usedExtendedUserCarePlanItems) {

      if (this.colorMap.has(used.vetReservationUUID) && this.colorMap.get(used.vetReservationUUID) === '') {
        this.colorMap.set(used.vetReservationUUID, this.colorList[colorMapIndex++]);
        if (colorMapIndex === this.colorList.length) {
          colorMapIndex = 0;
        }
      } else if (!this.colorMap.has(used.vetReservationUUID)) {
        this.colorMap.set(used.vetReservationUUID, '');
      }
    }
  }

  onCancellationPopoverDismiss() {
    this.cancellationReason = '';
  }

  canBeCanceled(userCarePlanItem: UserCarePlanItem) {
    return !userCarePlanItem?.restoreRequestCreated;
  }

  waitingForDecision(userCarePlanItem: UserCarePlanItem) {
    return userCarePlanItem?.restoreRequestCreated && !userCarePlanItem.restoreRequestAccepted && !userCarePlanItem.restoreRequestDeclined;
  }

  restoreAccepted(userCarePlanItem: UserCarePlanItem) {
    return userCarePlanItem?.restoreRequestCreated && userCarePlanItem.restoreRequestAccepted;
  }

  restoreDeclined(userCarePlanItem: UserCarePlanItem) {
    return userCarePlanItem?.restoreRequestCreated && userCarePlanItem.restoreRequestDeclined;
  }
}
